/** Structured editors for assessment questions and storage variants. */

export const DEFAULT_AGE_OPTION_ROWS = [
  { value: "1-3Months", label: "Less than 3 months", impact: "0" },
  { value: "3-6Months", label: "3–6 months", impact: "6" },
  { value: "6-11Months", label: "6–11 months", impact: "11" },
  { value: "11Months & Above", label: "Over 11 months", impact: "21" },
];

export function emptyOptionRow() {
  return { value: "", label: "", impact: "0" };
}

export function emptyStorageRow() {
  return { storage: "", price: "0" };
}

/** Build rows from API question (supports legacy data without labels). */
export function optionRowsFromQuestion(question) {
  const options = question?.options || [];
  const impacts = question?.optionImpacts || {};
  const labels = question?.optionLabels || {};
  if (options.length === 0) {
    return [emptyOptionRow()];
  }
  return options.map((value) => ({
    value,
    label: labels[value] || value,
    impact: impacts[value] != null ? String(impacts[value]) : "0",
  }));
}

/** Parse legacy comma strings into rows (options + impacts). */
export function optionRowsFromLegacy(optionsStr, impactsStr) {
  const options = (optionsStr || "")
    .split(",")
    .map((o) => o.trim())
    .filter(Boolean);
  const impacts = parseOptionImpacts(impactsStr) || {};
  if (options.length === 0) {
    return [emptyOptionRow()];
  }
  return options.map((value) => ({
    value,
    label: value,
    impact: impacts[value] != null ? String(impacts[value]) : "0",
  }));
}

export function optionRowsToPayload(rows) {
  const options = [];
  const optionImpacts = {};
  const optionLabels = {};
  (rows || []).forEach((row) => {
    const value = row.value?.trim();
    if (!value) return;
    options.push(value);
    optionImpacts[value] = parseFloat(row.impact || "0") || 0;
    const label = row.label?.trim();
    if (label && label !== value) {
      optionLabels[value] = label;
    }
  });
  return {
    options,
    optionImpacts: Object.keys(optionImpacts).length ? optionImpacts : undefined,
    optionLabels: Object.keys(optionLabels).length ? optionLabels : undefined,
  };
}

export function parseOptionImpacts(str) {
  if (!str || !str.trim()) return undefined;
  const map = {};
  str.split(",").forEach((pair) => {
    const idx = pair.indexOf(":");
    if (idx <= 0) return;
    const option = pair.slice(0, idx).trim();
    const impact = pair.slice(idx + 1).trim();
    if (option && impact != null) map[option] = parseFloat(impact);
  });
  return Object.keys(map).length ? map : undefined;
}

export function formatOptionImpacts(map) {
  if (!map || typeof map !== "object") return "—";
  return Object.entries(map).map(([k, v]) => `${k}: ${v}%`).join(" · ");
}

export function storageRowsFromApi(options) {
  const list = options || [];
  if (list.length === 0) return [emptyStorageRow()];
  return list.map((o) => ({
    storage: String(o.storage ?? ""),
    price: o.price != null ? String(o.price) : "0",
  }));
}

export function storageRowsFromLegacy(str) {
  const rows = [];
  if (!str || !str.trim()) return [emptyStorageRow()];
  str.split(",").forEach((pair) => {
    const idx = pair.indexOf(":");
    if (idx <= 0) return;
    const storage = pair.slice(0, idx).trim();
    const price = pair.slice(idx + 1).trim();
    if (storage) rows.push({ storage, price: price || "0" });
  });
  return rows.length ? rows : [emptyStorageRow()];
}

export function storageRowsToMap(rows) {
  const map = {};
  (rows || []).forEach((row) => {
    const storage = row.storage?.trim();
    if (!storage) return;
    map[storage] = parseFloat(row.price || "0") || 0;
  });
  return map;
}

export function storageRowsToLegacyString(rows) {
  return (rows || [])
    .filter((r) => r.storage?.trim())
    .map((r) => `${r.storage.trim()}:${r.price ?? 0}`)
    .join(",");
}

export function formatStorageSummary(options) {
  return (options || [])
    .map((o) => {
      const gb = String(o.storage ?? "").trim();
      const label = /gb/i.test(gb) ? gb : `${gb} GB`;
      const price = Number(o.price ?? 0);
      return price > 0 ? `${label} (₹${price})` : label;
    })
    .join(", ") || "—";
}
